import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetFortnoxIntegrationConfigQuery } from "src/common/service/fusionHub/integrations/fortnoxApi";
import {
	OPTIONAL_SCOPES,
	type OptionalScope,
} from "src/features/integrations/sources/fortnox/constants";
import { FortnoxScopeSelection } from "src/features/integrations/sources/fortnox/FortnoxScopeSelection";
import { OAuthAuthorizationView } from "src/features/integrations/sources/oauth/OAuthAuthorizationView";

interface FortnoxAuthorizationModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	description?: string;
	showModeSelection?: boolean;
	initialMode?: "redirect" | "copy";
	isLoading: boolean;
	onAuthorize: (
		mode: "redirect" | "copy",
		scopes: string[],
	) => Promise<string | null>;
	integrationId?: string;
}

export const FortnoxAuthorizationModal = ({
	isOpen,
	onClose,
	title,
	description,
	showModeSelection = false,
	initialMode = "redirect",
	isLoading,
	onAuthorize,
	integrationId,
}: FortnoxAuthorizationModalProps) => {
	const { t } = useTranslation();
	const [mode, setMode] = useState<"redirect" | "copy">(initialMode);
	const [selectedScopes, setSelectedScopes] = useState<OptionalScope[]>([]);

	const {
		currentData: config,
		isError: isConfigError,
		refetch: refetchConfig,
	} = useGetFortnoxIntegrationConfigQuery(
		integrationId ? { integrationId } : skipToken,
	);

	const isLoadingScopes = Boolean(integrationId) && !config && !isConfigError;

	useEffect(() => {
		setMode(initialMode);
	}, [initialMode]);

	useEffect(() => {
		if (isOpen && config) {
			setSelectedScopes(
				OPTIONAL_SCOPES.filter((scope) =>
					config.additionalScopes.includes(scope),
				),
			);
		} else if (isOpen && !integrationId) {
			setSelectedScopes([]);
		}
	}, [isOpen, config, integrationId]);

	const handleSubmit = async () => {
		const authorizationUrl = await onAuthorize(mode, selectedScopes);
		if (!authorizationUrl) return;

		if (mode === "redirect") {
			location.href = authorizationUrl;
		} else {
			try {
				await navigator.clipboard.writeText(authorizationUrl);
				showSuccessNotification({
					message: t("The link was copied to clipboard"),
				});
				onClose();
			} catch {
				showErrorNotification({
					message: t("Failed to copy authentication link"),
				});
			}
		}
	};

	return (
		<OAuthAuthorizationView
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			description={description}
			providerName="Fortnox"
			showModeSelection={showModeSelection}
			mode={mode}
			onModeChange={setMode}
			isLoading={isLoading || isLoadingScopes}
			onSubmit={handleSubmit}
			submitDisabled={isConfigError}
		>
			<FortnoxScopeSelection
				selectedScopes={selectedScopes}
				onChange={setSelectedScopes}
				isLoading={isLoadingScopes}
				isError={isConfigError}
				onRetry={refetchConfig}
			/>
		</OAuthAuthorizationView>
	);
};
